import { Alert, AlertTitle, Checkbox, FormControl, FormControlLabel, FormHelperText, FormLabel, Stack, TextField, Typography, styled } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import type { ChangeEvent, Dispatch, SetStateAction, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type ProductOptionStore from '../../../AppData/_stores/ProductOptionStore';
import type ProductStore from '../../../AppData/_stores/ProductStore';
import type { UserProductDesignStore } from '../../../UserData/_stores/UserProductDesignStore';
import type ListingStore from '../../../UserData/_stores/Store/ListingStore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SquarespaceStorePageField from './SquarespaceStorePageField';
import { StoreListingValidation, type StoreListingValidationResult } from './Utils/StoreListingValidation';
import useStoreIntegrationName from '../../../Common/_hooks/Store/useStoreIntegrationName';
import ProductMockupGroup from './ProductMockupGroup';

export interface Props {
	validation: StoreListingValidationResult
	store: string
	listing: ListingStore
	onChangeListing: Dispatch<SetStateAction<ListingStore>>

	index?: number
	handleStep?: (step: number) => void
	labExit?: boolean
	massPresets?: boolean
	productDesign?: UserProductDesignStore
	product: ProductStore
	showStorePageField?: boolean
}

const PreviewImage = styled('img')(({ theme }) => ({
	width: '33%',
	height: 'auto',
	borderRadius: theme.shape.borderRadius,
	display: 'none',

	[theme.breakpoints.up('sm')]: {
		display: 'block',
	}
}));

const CheckboxFormHelperText = styled(FormHelperText)(({ theme }) => ({
	marginLeft: 0,
	marginTop: theme.spacing(-0.5),
}))

interface OptionGroup {
	name: string
	listOptions: ProductOptionStore[]
}

interface OptionTypeGroup {
	id: number
	name: string
	options: Map<string, OptionGroup>
	selectedCount: number
}

export default function EditProductDetails(props: Props) {
	const [t] = useTranslation('account-v2')
	const storeLabel = useStoreIntegrationName(props.store)

	const onChangeOption = useCallback((listIds: number[], checked: boolean) => {
		props.onChangeListing(listing => {
			const optionIdSet = new Set(listIds);
			let disabledOptionIds = listing.get('disabled_option_ids').filter(id => !optionIdSet.has(id));

			if (!checked) {
				listIds.forEach(id => {
					if (!disabledOptionIds.includes(id)) {
						disabledOptionIds = disabledOptionIds.push(id)
					}
				})
			}

			return listing
				.set('disabled_option_ids', disabledOptionIds)
		})
	}, [props.onChangeListing])

	const onChangeMockup = useCallback((id: number, checked: boolean) => {
		props.onChangeListing(listing => {
			let disabledPreviewIds = listing.get('disabled_preview_ids').filter(previewId => previewId !== id);

			if (!checked) {
				disabledPreviewIds = disabledPreviewIds.push(id);
			}

			return listing.set('disabled_preview_ids', disabledPreviewIds)
		})
	}, [props.onChangeListing])

	const previews = useMemo(() => {
		if (props.massPresets) {
			return props.product.get('previews');
		}

		return props.productDesign?.get('previews').map(preview => preview.set(
			'deleted',
			props.product.get('previews').find(productPreview => productPreview.get('id') === preview.get('id'))?.get('deleted') ?? false,
		));
	}, [props.massPresets, props.product, props.productDesign])

	const previewGroups = useMemo(() => {
		if (!previews) {
			return [];
		}

		// Initial page data uses 0/1 flags; AJAX-loaded previews use booleans.
		return [false, true].map(legacy => ({
			legacy,
			previews: previews.valueSeq().filter(preview => Boolean(preview.get('deleted')) === legacy),
		})).filter(group => group.previews.count() > 0);
	}, [previews]);

	const onChangeUpdatePreset = useCallback((e: SyntheticEvent, checked: boolean) => {
		props.onChangeListing(listing => listing.set('update_preset', checked))
	}, [props.onChangeListing])

	const onChangeStorePage = useCallback((value: string) => {
		props.onChangeListing(listing => listing.set('squarespace_store_page_id', value))
	}, [props.onChangeListing])

	const onChangeTitle = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		props.onChangeListing(listing => listing.set('title', e.target.value))
	}, [props.onChangeListing])

	const onChangeDescription = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		props.onChangeListing(listing => listing.set('description', e.target.value))
	}, [props.onChangeListing])

	const thumbnail = useMemo(() => {
		return props.massPresets ? props.product.get('previews').first()?.get('url') : props.productDesign?.get('thumbnail')
	}, [props.massPresets, props.product, props.productDesign]);

	const nbPreviews = useMemo(() => {
		return props.massPresets ? props.product.get('previews').count() : props.productDesign?.get('previews').count() ?? 0;
	}, [props.massPresets, props.product, props.productDesign]);

	const availableOptionTypes = useMemo(() => {
		return props.massPresets ? props.product.get('availableOptionTypes') : props.productDesign?.get('availableOptionTypes');
	}, [props.massPresets, props.product, props.productDesign]);

	const disabledOptionIds = props.listing.get('disabled_option_ids');
	const disabledPreviewIds = props.listing.get('disabled_preview_ids');

	const optionTypeGroups = useMemo(() => {
		const groups: OptionTypeGroup[] = [];

		availableOptionTypes?.valueSeq().forEach(type => {
			if (!type.get('visible') || type.get('options').count() <= 1) {
				return;
			}

			const options = new Map<string, OptionGroup>();
			type.get('options').forEach((option) => {
				if (options.has(option.get('slug'))) {
					options.get(option.get('slug'))?.listOptions.push(option);
					return;
				}

				options.set(option.get('slug'), {
					name: option.get('name'),
					listOptions: [option],
				});
			});

			const selectedCount = Array.from(options.values()).filter((optionGroup) => {
				return optionGroup.listOptions.some(option => !disabledOptionIds.includes(option.get('id')))
			}).length;

			groups.push({
				id: type.get('id'),
				name: type.get('name'),
				options,
				selectedCount,
			});
		});

		return groups;
	}, [availableOptionTypes, disabledOptionIds]);

	const selectedPreviewCount = useMemo(() => {
		return previews?.count(preview => !disabledPreviewIds.includes(preview.get('id'))) ?? 0;
	}, [disabledPreviewIds, previews])

	const variantLimit = props.validation.fieldErrors.listing?.variantLimit;
	const variantLimitError = useMemo(() => {
		if (!variantLimit) {
			return undefined;
		}

		return variantLimit.requiresCompleteOptionMatrix
			? t('{{store}} allows up to {{max}} variation rows per listing. Please remove some options to continue.', {
				store: storeLabel,
				max: variantLimit.max,
			})
			: t('{{store}} allows up to {{max}} variants per listing. Remove some options to continue.', {
				store: storeLabel,
				max: variantLimit.max,
			});
	}, [storeLabel, t, variantLimit]);

	return <>
		<Stack spacing={4}>
			<Stack spacing={1}>
				<Typography variant="h3">{props.massPresets ? t('Edit Preset Details') : t('Edit Product Details')}</Typography>

				<Typography variant="body1" sx={{ mb: 2 }}>{t('Edit product details for this listing.')}</Typography>
			</Stack>

			{props.showStorePageField ? <SquarespaceStorePageField
				value={props.listing.get('squarespace_store_page_id') ?? ''}
				onChange={onChangeStorePage}
				error={!!props.validation.fieldErrors.storePage}
			/> : null}

			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				spacing={3}
				sx={{
					alignItems: {
						xs: 'stretch',
						sm: 'flex-start',
					}
				}}
			>
				<PreviewImage src={thumbnail} alt=""></PreviewImage>
				<Stack spacing={2} sx={{ flexGrow: 1 }}>
					{!props.massPresets ? <TextField
						label={t("Product title")}
						value={props.listing.get('title')}
						onChange={onChangeTitle}
						error={!!props.validation.fieldErrors.title}
						helperText={props.validation.fieldErrors.title ? t('Please enter a product title.') : undefined}
					/> : null}
					<TextField
						multiline
						rows={7}
						label={t("Product Description")}
						value={props.listing.get('description')}
						onChange={onChangeDescription}
						error={!!props.validation.fieldErrors.description}
						helperText={props.validation.fieldErrors.description ? t('Please enter a product description.') : undefined}
					/>
				</Stack>
			</Stack>
			<Stack spacing={2}>
				{optionTypeGroups.map(type => {
					const optionTypeError = props.validation.fieldErrors[StoreListingValidation.getOptionTypeField(type.id)];

					return <FormControl key={type.id} error={!!optionTypeError}>
						<Stack
							direction="row"
							alignItems="baseline"
							spacing={0.75}
						>
							<FormLabel>{type.name}</FormLabel>
							<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>({type.selectedCount + '/' + type.options.size})</Typography>
						</Stack>
						<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
							{Array.from(type.options).map(([optionSlug, optionGroup]) => {
								const enabledOptions = optionGroup.listOptions.filter((option) => {
									return !disabledOptionIds.includes(option.get('id'));
								});
								const checked = enabledOptions.length > 0;
								const indeterminate = checked && enabledOptions.length < optionGroup.listOptions.length;
								const listOptionIds = optionGroup.listOptions.map((option) => option.get('id'));

								return <FormControlLabel
									key={optionSlug}
									control={<Checkbox
										color="secondary"
										checked={checked}
										indeterminate={indeterminate}
										onChange={(e, checked) => {
											onChangeOption(listOptionIds, checked);
										}}
									/>}
									label={optionGroup.name}
								/>
							})}
						</Stack>
						{optionTypeError ? <CheckboxFormHelperText error>{t('Please select at least one option.')}</CheckboxFormHelperText> : null}
					</FormControl>
				})}

				{variantLimitError ? <CheckboxFormHelperText error>{variantLimitError}</CheckboxFormHelperText> : null}

				{previews ? <FormControl error={!!props.validation.fieldErrors.mockups}>
					<Stack
						direction="row"
						alignItems="baseline"
						spacing={0.75}
						sx={{ mb: 1 }}
					>
						<FormLabel>{t('Mockups')}</FormLabel>
						<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>({selectedPreviewCount + '/' + nbPreviews})</Typography>
					</Stack>
					<Stack spacing={2}>
						{previewGroups.map(group => <ProductMockupGroup
							key={String(group.legacy)}
							previews={group.previews}
							disabledPreviewIds={disabledPreviewIds}
							onChangeMockup={onChangeMockup}
							legacy={group.legacy}
							showHeading={previewGroups.length > 1 || group.legacy}
						/>)}
					</Stack>
					{props.validation.fieldErrors.mockups ? <CheckboxFormHelperText error sx={{mt: 0.5}}>{t('Please select at least one mockup.')}</CheckboxFormHelperText> : null}
				</FormControl> : null}
			</Stack>

			{!props.massPresets ? <Alert
				severity="info"
				icon={<HelpOutlineIcon fontSize="inherit" />}
				sx={{
					'.MuiAlert-message': {
						overflow: 'visible'
					}
				}}
			>
				<AlertTitle>{t('Would you like to update the preset for this product?')}</AlertTitle>
				<FormControlLabel
					control={<Checkbox
						sx={{
							color: 'inherit',
							'&.Mui-checked': {
								color: 'inherit',
							}
						}}
					/>}
					checked={props.listing.get('update_preset')}
					label={t('Update product preset with the above details')}
					onChange={onChangeUpdatePreset}
				/>
			</Alert> : null}
		</Stack>
	</>;
}
